import { Helmet } from "react-helmet-async";

const SITE_URL = "https://semperfipumps.com";
const BUSINESS_NAME = "Semper Fi Well & Pump Services";
const BUSINESS_PHONE = "+18665677867";

interface JsonLdProps {
  data: Record<string, unknown> | Record<string, unknown>[];
}

/** Inject one or more JSON-LD blocks into <head> via react-helmet-async. */
export const JsonLd = ({ data }: JsonLdProps) => {
  const blocks = Array.isArray(data) ? data : [data];
  return (
    <Helmet>
      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

interface Crumb {
  name: string;
  path: string; // e.g. "/services/well-pump-repair"
}

export const breadcrumbSchema = (crumbs: Crumb[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: `${SITE_URL}${c.path === "/" ? "" : c.path}`,
  })),
});

interface ServiceSchemaArgs {
  name: string;
  description: string;
  path: string;
  serviceType?: string;
}

export const serviceSchema = ({ name, description, path, serviceType }: ServiceSchemaArgs) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  serviceType: serviceType ?? name,
  url: `${SITE_URL}${path}`,
  provider: {
    "@type": "LocalBusiness",
    name: BUSINESS_NAME,
    telephone: BUSINESS_PHONE,
    address: {
      "@type": "PostalAddress",
      streetAddress: "119 Lacey Ln",
      addressLocality: "Saylorsburg",
      addressRegion: "PA",
      postalCode: "18353",
      addressCountry: "US",
    },
    areaServed: ["Monroe County", "Pike County", "Carbon County", "Northampton County"],
  },
  areaServed: [
    { "@type": "AdministrativeArea", name: "Monroe County, PA" },
    { "@type": "AdministrativeArea", name: "Pike County, PA" },
    { "@type": "AdministrativeArea", name: "Carbon County, PA" },
    { "@type": "AdministrativeArea", name: "Northampton County, PA" },
  ],
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5",
    reviewCount: "150",
  },
});

interface ArticleSchemaArgs {
  title: string;
  description: string;
  path: string;
  datePublished?: string;
  dateModified?: string;
}

export const articleSchema = ({
  title,
  description,
  path,
  datePublished = "2024-01-01",
  dateModified,
}: ArticleSchemaArgs) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${path}` },
  url: `${SITE_URL}${path}`,
  datePublished,
  dateModified: dateModified ?? datePublished,
  author: { "@type": "Organization", name: BUSINESS_NAME, url: SITE_URL },
  publisher: {
    "@type": "Organization",
    name: BUSINESS_NAME,
    url: SITE_URL,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
  },
});

interface FaqItem {
  q: string;
  a: string;
}

export const faqPageSchema = (items: FaqItem[], path = "/faq") => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${SITE_URL}${path}#faq`,
  url: `${SITE_URL}${path}`,
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  })),
});

interface ReviewItem {
  name: string;
  location: string;
  text: string;
  rating: number;
}

export const reviewsSchema = (reviews: ReviewItem[]) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: BUSINESS_NAME,
  url: `${SITE_URL}/reviews`,
  telephone: BUSINESS_PHONE,
  address: {
    "@type": "PostalAddress",
    streetAddress: "119 Lacey Ln",
    addressLocality: "Saylorsburg",
    addressRegion: "PA",
    postalCode: "18353",
    addressCountry: "US",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5",
    reviewCount: String(Math.max(reviews.length, 150)),
    bestRating: "5",
    worstRating: "1",
  },
  review: reviews.map((r) => ({
    "@type": "Review",
    reviewRating: {
      "@type": "Rating",
      ratingValue: String(r.rating),
      bestRating: "5",
    },
    author: { "@type": "Person", name: r.name },
    reviewBody: r.text,
    locationCreated: { "@type": "Place", name: r.location },
  })),
});
